<script lang="ts">
  import { nameProblem } from '@arena/shared';
  import { onMount } from 'svelte';
  import * as THREE from 'three';
  import { bass, music, musicTime } from '../game/Sfx';
  import { signInWithDiscord, startGuest } from '../lib/auth';
  import { REGIONS, goToRegion, region } from '../lib/region';

  let { onenter }: { onenter: () => void } = $props();
  let name = $state('');
  let discord = $state(false);
  let busy = $state(false);
  let failure = $state('');

  /** The arena song's opening drum hits, in seconds: each brings in the next part of the card. */
  const BEATS = [0.4, 1.6, 2.75, 4];
  /** How far above its recent average the bass must jump to count as a hit, and how soon another may follow (s). */
  const HIT = 0.05;
  const HIT_GAP = 0.4;
  /** Hits only pulse this far into the song (s), so the glow plays over the intro and again each time it loops. */
  const GLOWING = 20;
  /** The glow a hit sets off: its strength (it peaks at about 0.7 of this), how fast it swells, and how fast it fades (s). */
  const PULSE = 0.7;
  const PULSE_RISE = 0.12;
  const PULSE_FADE = 0.35;
  /** Embers rising behind the card: where each starts (%), how far it drifts sideways (px), its size (px), and its timing (s). */
  const EMBERS = Array.from({ length: 40 }, () => ({
    x: Math.random() * 100,
    drift: (Math.random() - 0.5) * 160,
    size: 2 + Math.random() * 3,
    duration: 8 + Math.random() * 10,
    delay: -Math.random() * 18,
  }));
  let begun = $state(false);
  let stage = $state(0);
  let glow = $state(0);
  let input = $state<HTMLInputElement>();
  let stop: (() => void) | undefined;

  onMount(() => {
    const refused = new URLSearchParams(location.search).get(
      'error_description',
    );
    if (refused) {
      failure = refused;
      history.replaceState(null, '', location.pathname + location.hash);
    }
    fetch('/api/config')
      .then((r) => r.json())
      .then((c) => (discord = c.discord));
    return () => stop?.();
  });

  $effect(() => {
    if (stage === BEATS.length) input?.focus();
  });

  /** The gesture browsers want before playing sound; the intro runs off the song from here. */
  function begin() {
    begun = true;
    void THREE.AudioContext.getContext().resume();
    const start = performance.now() / 1000;
    let average = 0;
    let loud = false;
    let hit = -Infinity;
    let last = start;
    let frame = 0;
    const tick = () => {
      music('arena');
      const now = performance.now() / 1000;
      const level = bass();
      average += (level - average) * 0.02;
      // A drum hit is the bass jumping above its recent average; each one plays the same pulse.
      const wasLoud = loud;
      loud = level - average > HIT;
      if (loud && !wasLoud && now - hit > HIT_GAP && musicTime() < GLOWING)
        hit = now;
      const since = now - hit;
      // A hit before the last pulse has faded swells from where it is, rather than flickering to 0.
      glow = Math.max(
        glow * Math.exp(-(now - last) / PULSE_FADE),
        PULSE * Math.min(1, since / PULSE_RISE) * Math.exp(-since / PULSE_FADE),
      );
      last = now;
      // Should the song load slowly or not at all, the card still comes in.
      const t = Math.max(musicTime(), now - start - 2);
      stage = BEATS.filter((beat) => t >= beat).length;
      frame = requestAnimationFrame(tick);
    };
    tick();
    stop = () => cancelAnimationFrame(frame);
  }

  async function enter() {
    busy = true;
    try {
      await startGuest();
      onenter();
    } catch (e) {
      failure = e instanceof Error ? e.message : String(e);
      busy = false;
    }
  }

  function discordSignIn(event: SubmitEvent) {
    event.preventDefault();
    const problem = name.trim()
      ? nameProblem(name.trim())
      : 'Enter a name first.';
    if (!problem) return void signInWithDiscord(name.trim());
    failure = problem;
    input?.focus();
  }
</script>

<div class="backdrop" style:--glow={glow}>
  <div class="embers" aria-hidden="true">
    {#each EMBERS as ember}
      <span
        style:left="{ember.x}%"
        style:--size="{ember.size}px"
        style:--drift="{ember.drift}px"
        style:animation-duration="{ember.duration}s"
        style:animation-delay="{ember.delay}s"
      ></span>
    {/each}
  </div>
  {#if !begun}
    <button class="begin" data-quiet onclick={begin}>
      <span class="title">SANDSWORN</span>
      <span class="hint">Click to enter</span>
    </button>
  {:else}
    <form
      class="panel card reveal"
      class:shown={stage >= 1}
      onsubmit={discordSignIn}
    >
      <h1 aria-label="Sandsworn">
        <img
          class="word"
          class:shown={stage >= 1}
          src="/wordmark/sand.webp"
          alt=""
        />
        <img
          class="word"
          class:shown={stage >= 2}
          src="/wordmark/sworn.webp"
          alt=""
        />
      </h1>
      {#if discord}
        <p class="question reveal" class:shown={stage >= 3}>
          What is your name?
        </p>
      {/if}
      <div class="rest reveal" class:shown={stage >= 4}>
        {#if discord}
          <input
            bind:this={input}
            bind:value={name}
            maxlength="24"
            placeholder="Your name"
            autocomplete="off"
          />
          <button class="discord">Sign in with Discord</button>
          <div class="or">— or —</div>
        {/if}
        <button type="button" class="primary" disabled={busy} onclick={enter}
          >Enter as guest</button
        >
        <p class="small muted">
          Guests play as a numbered Guest, kept with their rating on this
          browser.
        </p>
        {#if region}
          <label class="small muted">
            Region
            <select
              value={region}
              onchange={(e) => goToRegion(e.currentTarget.value)}
            >
              {#each Object.entries(REGIONS) as [id, label] (id)}
                <option value={id}>{label}</option>
              {/each}
            </select>
          </label>
        {/if}
        {#if failure}<p class="small error">{failure}</p>{/if}
        <p class="small muted">
          By playing you agree to the <a href="/terms.html" target="_blank"
            >Terms</a
          >
          and <a href="/privacy.html" target="_blank">Privacy Policy</a>.
        </p>
      </div>
    </form>
  {/if}
</div>

<style>
  .backdrop {
    height: 100%;
    display: grid;
    place-items: center;
    background:
      radial-gradient(ellipse at top, #3a2f18 0%, transparent 65%), var(--bg);
  }
  .backdrop::after {
    content: '';
    position: fixed;
    inset: 0;
    pointer-events: none;
    box-shadow: inset 0 0 calc(40px + var(--glow) * 160px)
      calc(var(--glow) * 20px)
      color-mix(in srgb, var(--gold) calc(var(--glow) * 60%), transparent);
  }
  .embers {
    position: fixed;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
    filter: brightness(calc(1 + var(--glow) * 1.5));
  }
  .embers span {
    position: absolute;
    bottom: -10px;
    width: var(--size);
    height: var(--size);
    border-radius: 50%;
    background: #ffb347;
    box-shadow: 0 0 calc(var(--size) * 2) #ff7a1a;
    opacity: 0;
    animation: rise linear infinite;
  }
  @keyframes rise {
    10% {
      opacity: 0.9;
    }
    70% {
      opacity: 0.6;
    }
    to {
      transform: translate(var(--drift), -105vh) scale(0.4);
      opacity: 0;
    }
  }
  .begin {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 16px;
    background: none;
    border: none;
  }
  .title {
    letter-spacing: 16px;
    font-size: 48px;
    font-weight: bold;
    opacity: 0.4;
  }
  .hint {
    color: var(--muted);
    letter-spacing: 4px;
    text-transform: uppercase;
    animation: breathe 2.4s ease-in-out infinite;
  }
  @keyframes breathe {
    50% {
      opacity: 0.3;
    }
  }
  .card {
    position: relative;
    width: 340px;
    padding: 32px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    text-align: center;
    border-color: var(--gold);
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.6);
  }
  .rest {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .reveal {
    opacity: 0;
    visibility: hidden;
    transform: scale(1.15);
    transition:
      opacity 0.4s,
      transform 0.4s ease-out,
      visibility 0.4s;
  }
  .reveal.shown {
    opacity: 1;
    visibility: visible;
    transform: none;
  }
  h1 {
    margin: 0 0 4px;
    filter: drop-shadow(
      0 0 calc(var(--glow) * 18px) rgba(255, 179, 71, calc(var(--glow) * 1.2))
    );
  }
  /* Each word rises out of the dark: a black, blurred silhouette that lights up to gold. */
  .word {
    display: block;
    width: 100%;
    opacity: 0;
    visibility: hidden;
    filter: brightness(0) blur(6px);
    transform: scale(0.94);
    transition:
      opacity 0.5s,
      filter 1.2s ease-out,
      transform 1.2s ease-out,
      visibility 0.5s;
  }
  .word.shown {
    opacity: 1;
    visibility: visible;
    filter: none;
    transform: none;
  }
  .question {
    margin: 0;
    font-style: italic;
    color: var(--muted);
  }
  input {
    text-align: center;
    font-size: 16px;
    padding: 10px;
  }
  .primary {
    padding: 10px;
    font-size: 16px;
    font-weight: bold;
  }
  .or {
    font-size: 11px;
    letter-spacing: 2px;
    color: var(--muted);
  }
  .discord {
    background: #5865f2;
    border-color: #5865f2;
    font-weight: bold;
  }
  .small {
    font-size: 12px;
    margin: 0;
  }
  .muted {
    color: var(--muted);
  }
  .muted a {
    color: inherit;
  }
  .error {
    color: var(--enemy);
  }
  @media (prefers-reduced-motion: reduce) {
    .backdrop::after,
    .embers {
      display: none;
    }
    h1 {
      filter: none;
    }
    .reveal,
    .word,
    .hint {
      transition: none;
      animation: none;
    }
  }
</style>
